import { useCallback, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  GROUP_LOCATIONS_QUERY_KEY,
  invalidatePriorityQueue,
  useGetBuyerInventoryGroupLocations,
  useUpsertBuyerInventoryConfiguration,
  useUpsertInventory,
} from '~/api';
import type { PriorityGroup, GroupLocation } from '~/api';
import { useTable } from '~/components/ui/table';
import type {
  Inventory,
  UpsertBuyerInventoryConfigurationMutation,
} from '~/graphql/graphql';
import { useDebounce } from '~/hooks/useDebounce';
import type { SortDirection } from '~/components/ui/table';

type SubRowInventory = NonNullable<GroupLocation['inventory']>;

export function groupLocationKey(facilityId: string, pplId: string): string {
  return `${facilityId}_${pplId}`;
}

export function usePriorityGroupSubRows(
  groups: PriorityGroup[],
  expandedGroupIds: ReadonlySet<string>,
  onSort: (key: string, direction: SortDirection) => void
) {
  const queryClient = useQueryClient();
  const upsertInventory = useUpsertInventory();
  const upsertInventoryConfiguration = useUpsertBuyerInventoryConfiguration();

  const [showEditInventory, setShowEditInventory] = useState(false);
  const [editingInventory, setEditingInventory] = useState<Inventory | null>(
    null
  );
  const editingGroupKeyRef = useRef<{
    facilityId: string;
    pplId: string;
  } | null>(null);

  const expandedGroupKeys = useMemo(
    () =>
      groups.flatMap((group) =>
        expandedGroupIds.has(group.id)
          ? [
              {
                facilityId: group.facility.id,
                pplId: group.platformProductsLength.id,
              },
            ]
          : []
      ),
    [groups, expandedGroupIds]
  );

  const locationResults = useGetBuyerInventoryGroupLocations(expandedGroupKeys);
  const locationsByGroupKey = useMemo(() => {
    const byKey = new Map<string, (typeof locationResults)[number]>();
    expandedGroupKeys.forEach(({ facilityId, pplId }, index) => {
      byKey.set(groupLocationKey(facilityId, pplId), locationResults[index]);
    });
    return byKey;
  }, [expandedGroupKeys, locationResults]);

  // Debounced so a burst of inline saves coalesces into one re-aggregation; the
  // sub-row cache is patched immediately for feedback.
  const invalidateQueue = useDebounce(() => {
    invalidatePriorityQueue(queryClient);
  }, 1000);

  const patchSubRowInventory = (
    facilityId: string,
    pplId: string,
    inventoryId: string,
    patch: (inventory: SubRowInventory) => SubRowInventory
  ) => {
    queryClient.setQueryData(
      [GROUP_LOCATIONS_QUERY_KEY, facilityId, pplId],
      (old: { buyerInventoryGroupLocations: GroupLocation[] } | undefined) =>
        old && {
          buyerInventoryGroupLocations: old.buyerInventoryGroupLocations.map(
            (row) =>
              row.inventory?.id === inventoryId
                ? { ...row, inventory: patch(row.inventory) }
                : row
          ),
        }
    );
  };

  const handleTableEditSubmit = (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ): void => {
    const [groupId, inventoryId] = rowId.split('::');
    if (!inventoryId) return;
    const group = groups.find((g) => g.id === groupId);
    if (!group) return;
    const facilityId = group.facility.id;
    const pplId = group.platformProductsLength.id;
    const row = locationsByGroupKey
      .get(groupLocationKey(facilityId, pplId))
      ?.data?.buyerInventoryGroupLocations.find(
        (subRow) => subRow.inventory?.id === inventoryId
      );
    if (!row) return;
    const platformProductsLengthId = group.platformProductsLength.id;
    const locationId = row.location.id;
    const unit = group.platformProductsLength.platformProduct.standardUnitsCode;

    if (columnKey === 'stock') {
      upsertInventory.mutate(
        {
          platformProductsLengthId,
          locationId,
          quantity: { value: Number(newValue), unit },
        },
        {
          onSuccess: (result) => {
            const upserted = result?.upsertInventory;
            const editedStock = { value: Number(newValue), unit };
            patchSubRowInventory(
              facilityId,
              pplId,
              inventoryId,
              (inventory) => ({
                ...inventory,
                quantity: upserted?.quantity ?? editedStock,
                availableStock: upserted?.availableStock ?? editedStock,
              })
            );
            tableControls?.handleEditDone?.();
            invalidateQueue();
          },
        }
      );
      return;
    }

    if (columnKey === 'usageRatePerWeek') {
      upsertInventoryConfiguration.mutate(
        {
          platformProductsLengthId,
          locationId,
          usageRatePerWeek: { value: Number(newValue), unit },
        },
        {
          onSuccess: (result) => {
            const newConfig = result?.upsertBuyerInventoryConfiguration;
            patchSubRowInventory(
              facilityId,
              pplId,
              inventoryId,
              (inventory) => ({
                ...inventory,
                buyerInventoryConfiguration:
                  newConfig ?? inventory.buyerInventoryConfiguration,
              })
            );
            tableControls?.handleEditDone?.();
            invalidateQueue();
          },
        }
      );
    }
  };

  const tableControls = useTable({
    onSort,
    onEditSubmit: handleTableEditSubmit,
  });

  const openEdit = useCallback(
    (inv: Inventory, facilityId: string, pplId: string) => {
      setEditingInventory(inv);
      editingGroupKeyRef.current = { facilityId, pplId };
      setShowEditInventory(true);
    },
    []
  );

  const closeEdit = useCallback(() => {
    setShowEditInventory(false);
    setEditingInventory(null);
    editingGroupKeyRef.current = null;
  }, []);

  const handleEditModalSaved = (
    savedConfig: UpsertBuyerInventoryConfigurationMutation['upsertBuyerInventoryConfiguration']
  ) => {
    const editingGroupKey = editingGroupKeyRef.current;
    if (editingInventory && editingGroupKey) {
      patchSubRowInventory(
        editingGroupKey.facilityId,
        editingGroupKey.pplId,
        editingInventory.id,
        (inventory) => ({
          ...inventory,
          buyerInventoryConfiguration:
            savedConfig ?? inventory.buyerInventoryConfiguration,
        })
      );
    }
    invalidateQueue();
  };

  return {
    locationsByGroupKey,
    tableControls,
    openEdit,
    editingInventory,
    showEditInventory,
    setShowEditInventory,
    closeEdit,
    handleEditModalSaved,
  };
}
